import { useEffect, useState, useRef, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import { toast } from "sonner";
import {
  LogOut, Bell, BellOff, MapPin, Navigation, Camera, PenLine, CheckCircle2,
  PlayCircle, RefreshCw, Loader2, Phone, X, ClipboardList, ChevronLeft, FileText, Download,
  Locate, LocateOff, CalendarOff, MessageSquare, QrCode, Settings, Waypoints, Cable,
} from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import MonterChat from "@/components/MonterChat";
import SpeedTest from "@/components/SpeedTest";
import { ObjectInfo } from "@/components/ObjectInfo";
import api, { API, formatApiError, dlToken } from "@/lib/api";
import { Compass } from "lucide-react";
import AntennaAim from "@/components/wifi/AntennaAim";
import { cacheTasks, getCachedTasks, cacheTask, getCachedTask, enqueue, flushOutbox, outboxSize, isNetworkError } from "@/lib/offlineQueue";
import { useAuth } from "@/context/AuthContext";
import { enablePush, disablePush, pushSupported, registerServiceWorker } from "@/lib/push";
import { compressImage } from "@/lib/imageCompress";
import "leaflet/dist/leaflet.css";

const pin = (color) => L.divIcon({
  className: "",
  html: `<div style="background:${color};width:22px;height:22px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.5)"></div>`,
  iconSize: [22, 22], iconAnchor: [11, 22],
});
const STATUS = {
  zaplanowane: { label: "Zaplanowane", cls: "bg-slate-600 text-slate-100" },
  podjete: { label: "W trakcie", cls: "bg-amber-500 text-black" },
  gotowe: { label: "Gotowe", cls: "bg-green-600 text-white" },
};
const TYPE_COLOR = { awaria: "#ef4444", montaz: "#22d3ee", prace: "#a78bfa" };

function groupByClosure(splices) {
  const g = {};
  (splices || []).forEach((s) => { (g[s.closure_number || "?"] = g[s.closure_number || "?"] || []).push(s); });
  return g;
}

function InstallBanner() {
  const [prompt, setPrompt] = useState(null);
  const [hidden, setHidden] = useState(false);
  const standalone = typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone);
  const isIOS = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);
  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    const onInstalled = () => setHidden(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);
  if (standalone || hidden) return null;
  if (!prompt && !isIOS) return null;
  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    const res = await prompt.userChoice;
    if (res.outcome === "accepted") setHidden(true);
    setPrompt(null);
  };
  return (
    <div className="bg-cyan-600/15 border-b border-cyan-500/40 px-4 py-2.5 flex items-center gap-3" data-testid="pwa-install-banner">
      <Download size={18} className="text-cyan-300 shrink-0" />
      {prompt ? (
        <>
          <span className="text-sm text-cyan-100 flex-1">Zainstaluj aplikację, aby dostawać powiadomienia także przy zamkniętej apce.</span>
          <button onClick={install} data-testid="pwa-install-btn" className="bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg px-3 py-1.5 text-sm font-semibold whitespace-nowrap">Zainstaluj</button>
        </>
      ) : (
        <span className="text-sm text-cyan-100 flex-1">Aby zainstalować na iPhone: przycisk <b>Udostępnij</b> → <b>Dodaj do ekranu głównego</b>.</span>
      )}
    </div>
  );
}

function LoginScreen() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(email.trim().toLowerCase(), password); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Błąd logowania"); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#0F172A] text-slate-100 flex flex-col items-center justify-center p-6 relative" data-testid="monter-login">
      <div className="absolute top-0 inset-x-0"><InstallBanner /></div>
      <img src="/icon-192.png" alt="FiberPass" className="w-20 h-20 rounded-2xl mb-4" />
      <h1 className="text-2xl font-bold mb-1">FiberPass Monter</h1>
      <p className="text-slate-400 text-sm mb-6">Zaloguj się kontem montera</p>
      <form onSubmit={submit} className="w-full max-w-sm space-y-3">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail" required
          data-testid="monter-login-email" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-base outline-none focus:ring-2 focus:ring-cyan-500" />
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Hasło" required
          data-testid="monter-login-password" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-base outline-none focus:ring-2 focus:ring-cyan-500" />
        <button type="submit" disabled={busy} data-testid="monter-login-submit"
          className="w-full bg-cyan-600 hover:bg-cyan-500 disabled:opacity-60 rounded-lg py-3 font-semibold flex items-center justify-center gap-2">
          {busy && <Loader2 size={18} className="animate-spin" />} Zaloguj
        </button>
      </form>
    </div>
  );
}

function SignaturePad({ onSave, onCancel }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const start = (e) => { drawing.current = true; draw(e); };
  const end = () => { drawing.current = false; const c = canvasRef.current; c && c.getContext("2d").beginPath(); };
  const draw = (e) => {
    if (!drawing.current) return;
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    const x = t.clientX - r.left, y = t.clientY - r.top;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#0f172a";
    ctx.lineTo(x, y); ctx.stroke(); ctx.beginPath(); ctx.moveTo(x, y);
  };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); };
  const save = () => canvasRef.current.toBlob((b) => onSave(b), "image/png");
  return (
    <div className="fixed inset-0 z-[2000] bg-black/70 flex items-end sm:items-center justify-center p-4" data-testid="signature-pad">
      <div className="bg-white rounded-xl w-full max-w-md p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-slate-800">Podpis klienta</h3>
          <button onClick={onCancel} className="text-slate-500" data-testid="signature-close"><X size={20} /></button>
        </div>
        <canvas ref={canvasRef} width={380} height={200}
          className="border-2 border-dashed border-slate-300 rounded-lg w-full touch-none bg-white"
          onMouseDown={start} onMouseMove={draw} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={draw} onTouchEnd={end} data-testid="signature-canvas" />
        <div className="flex gap-2 mt-3">
          <button onClick={clear} className="flex-1 bg-slate-200 text-slate-700 rounded-lg py-2.5 text-sm" data-testid="signature-clear">Wyczyść</button>
          <button onClick={save} className="flex-1 bg-cyan-600 text-white rounded-lg py-2.5 text-sm font-semibold" data-testid="signature-save">Zapisz podpis</button>
        </div>
      </div>
    </div>
  );
}

function TaskDetail({ task, onBack, onChanged }) {
  const [full, setFull] = useState(task);
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const [sig, setSig] = useState(false);
  const [viewClosure, setViewClosure] = useState(null);
  const [aimOpen, setAimOpen] = useState(false);
  const loadStartRef = useRef(0);
  const wpTouchedRef = useRef(0);
  const ci = full.client_info || {};
  const lat = ci.lat, lng = ci.lng;

  const load = useCallback(async () => {
    const started = Date.now(); loadStartRef.current = started;
    try {
      const { data } = await api.get(`/schedule/tasks/${task.id}`);
      // Nie nadpisuj świeżego postępu spawów starą odpowiedzią GET (wyścig przy pierwszym odhaczeniu).
      setFull((prev) => (wpTouchedRef.current > started ? { ...data, work_plan: prev.work_plan } : data));
      cacheTask(data);
    } catch (e) { const c = getCachedTask(task.id); if (c) setFull(c); }
    try {
      const { data } = await api.get(`/attachments?entity_type=task&entity_id=${task.id}`);
      setFiles(data || []);
    } catch (e) {}
  }, [task.id]);
  useEffect(() => { load(); }, [load]);

  const act = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/schedule/tasks/${task.id}/${action}`);
      setFull(data); onChanged && onChanged();
      if (action === "complete" && (full.work_plan?.splices?.length || full.work_plan?.cable?.geometry))
        toast.success("Zlecenie zakończone — spawy naniesione na mufy");
      else toast.success(action === "accept" ? "Zlecenie podjęte" : "Zlecenie zakończone");
    } catch (err) {
      if (isNetworkError(err)) {
        enqueue({ url: `/schedule/tasks/${task.id}/${action}` });
        window.dispatchEvent(new Event("fp-outbox"));
        toast.info("Brak sieci — akcję zapisano do synchronizacji");
      } else { toast.error(formatApiError(err.response?.data?.detail)); }
    }
    finally { setBusy(false); }
  };
  const uploadBlob = async (blob, name) => {
    const fd = new FormData();
    fd.append("entity_type", "task"); fd.append("entity_id", task.id);
    fd.append("file", blob, name);
    const res = await fetch(`${API}/upload`, { method: "POST", credentials: "include", headers: { "X-Requested-With": "FiberPass" }, body: fd });
    if (!res.ok) throw new Error("upload failed " + res.status);
    await load();
  };
  const onPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const c = await compressImage(f); await uploadBlob(c, c.name || "zdjecie.jpg"); toast.success("Zdjęcie wysłane"); }
    catch (err) { toast.error("Błąd wysyłania zdjęcia"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const onSig = async (blob) => {
    setSig(false); setBusy(true);
    try { await uploadBlob(blob, `podpis-${task.task_no}.png`); toast.success("Podpis zapisany"); }
    catch (err) { toast.error("Błąd zapisu podpisu"); }
    finally { setBusy(false); }
  };
  const toggleSplice = async (idx, done) => {
    wpTouchedRef.current = Date.now();
    // optymistyczna aktualizacja, aby uniknąć migotania i wyścigu z GET
    setFull((prev) => {
      const sp = [...((prev.work_plan?.splices) || [])];
      if (sp[idx]) sp[idx] = { ...sp[idx], done };
      return { ...prev, work_plan: { ...(prev.work_plan || {}), splices: sp } };
    });
    try {
      const { data } = await api.post(`/schedule/tasks/${task.id}/splice-progress`, { index: idx, done });
      wpTouchedRef.current = Date.now();
      setFull((prev) => ({ ...prev, work_plan: data.work_plan, status: data.status }));
      onChanged && onChanged();
    } catch (err) {
      if (isNetworkError(err)) {
        enqueue({ url: `/schedule/tasks/${task.id}/splice-progress`, body: { index: idx, done } });
        window.dispatchEvent(new Event("fp-outbox"));
        toast.info("Brak sieci — postęp zapisano do synchronizacji");
      } else { toast.error(formatApiError(err.response?.data?.detail)); load(); }
    }
  };
  const navigate = () => {
    if (lat == null || lng == null) { toast.error("Brak współrzędnych klienta"); return; }
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, "_blank");
  };
  const st = STATUS[full.status] || STATUS.zaplanowane;

  return (
    <div className="min-h-screen bg-[#0F172A] text-slate-100 pb-24" data-testid="monter-task-detail">
      <div className="sticky top-0 z-10 bg-slate-900 border-b border-slate-800 px-4 py-3 flex items-center gap-3">
        <button onClick={onBack} data-testid="task-back"><ChevronLeft size={24} /></button>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-slate-400">{full.task_no} · {full.type_label}</div>
          <div className="font-semibold truncate">{full.title}</div>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full ${st.cls}`}>{st.label}</span>
      </div>

      {lat != null && lng != null && (
        <div className="h-52 w-full" data-testid="task-map">
          <MapContainer center={[lat, lng]} zoom={16} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap contributors" />
            <Marker position={[lat, lng]} icon={pin(TYPE_COLOR[full.type] || "#22d3ee")}>
              <Popup>{ci.name}<br />{ci.address}</Popup>
            </Marker>
          </MapContainer>
        </div>
      )}

      <div className="p-4 space-y-4">
        {full.description && <p className="text-sm text-slate-300 whitespace-pre-wrap">{full.description}</p>}
        <div className="text-sm text-slate-400">
          <div>🗓 {full.scheduled_date} {full.scheduled_time}</div>
          {full.address && <div className="mt-1">📍 {full.address}</div>}
        </div>

        {ci.name && (
          <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 space-y-1.5 text-sm" data-testid="task-client-card">
            <div className="font-semibold text-cyan-300 mb-1">Klient</div>
            <div className="text-base font-medium">{ci.name}</div>
            {ci.address && <div className="text-slate-300">{ci.address}</div>}
            {ci.phone && <a href={`tel:${ci.phone}`} className="flex items-center gap-2 text-cyan-400"><Phone size={14} /> {ci.phone}</a>}
            {ci.contract_no && <div className="text-slate-400">Umowa: {ci.contract_no}</div>}
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 pt-2 text-slate-300">
              {ci.splitter_number && <div>Splitter: <b>{ci.splitter_number}</b>{ci.splitter_port ? ` / P${ci.splitter_port}` : ""}</div>}
              {ci.olt_name && <div>OLT: <b>{ci.olt_name}</b>{ci.pon_port ? ` · PON${ci.pon_port}` : ""}</div>}
              {ci.closures?.length > 0 && <div>Mufa: <b>{ci.closures.join(", ")}</b></div>}
              {ci.onu_mac && <div>ONU MAC: <b>{ci.onu_mac}</b></div>}
              {ci.measured_rx_dbm != null && <div>Rx: <b>{ci.measured_rx_dbm} dBm</b></div>}
              {ci.technology && <div>Tech: <b>{ci.technology}</b></div>}
            </div>
          </div>
        )}

        {full.work_plan?.splices?.length > 0 && (() => {
          const withIdx = full.work_plan.splices.map((s, i) => ({ ...s, _idx: i }));
          const doneCount = withIdx.filter((s) => s.done).length;
          const locked = full.status === "gotowe";
          return (
          <div className="bg-slate-800/60 border border-amber-700/50 rounded-xl p-4 space-y-2" data-testid="monter-splice-plan">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-semibold text-amber-300 flex items-center gap-2"><Waypoints size={16} /> Plan spawów</div>
              <span className="text-xs font-mono bg-slate-900/70 rounded px-2 py-0.5 text-green-300" data-testid="splice-progress-badge">{doneCount}/{withIdx.length}</span>
              <label data-testid="monter-splice-photo" className="ml-auto text-[11px] text-cyan-300 flex items-center gap-1 cursor-pointer">
                <Camera size={13} /> Zdjęcie mufy
                <input type="file" accept="image/*" capture="environment" onChange={onPhoto} className="hidden" />
              </label>
            </div>
            <p className="text-[11px] text-slate-400">Odhacz wykonane spawy — biuro widzi postęp. Wszystkie naniosą się na mufy po „Zakończ”.</p>
            {Object.entries(groupByClosure(withIdx)).map(([cnum, items]) => (
              <div key={cnum} className="bg-slate-900/50 rounded-lg p-2" data-testid={`splice-closure-${cnum}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-slate-200">Mufa {cnum}</span>
                  <button onClick={() => setViewClosure({ id: items[0].closure_id, number: cnum })} data-testid={`view-flow-${items[0].closure_id}`} className="text-[11px] text-cyan-300 underline">Zobacz rozpływ</button>
                </div>
                <div className="space-y-1">
                  {items.map((s) => (
                    <label key={s._idx} className="text-xs font-mono flex items-center gap-2.5 cursor-pointer py-1" data-testid="splice-plan-item">
                      <input type="checkbox" checked={!!s.done} disabled={locked || busy} onChange={(e) => toggleSplice(s._idx, e.target.checked)} data-testid={`splice-done-${s._idx}`} className="accent-green-500 shrink-0 w-5 h-5" />
                      <span className={s.done ? "text-slate-500 line-through" : "text-slate-300"}>{s.cable_in_number}</span><span className={s.done ? "text-slate-600" : "text-cyan-300"}>wł.{s.fiber_in}</span>
                      <span className="text-slate-500">↔</span>
                      <span className={s.done ? "text-slate-500 line-through" : "text-slate-300"}>{s.cable_out_number}</span><span className={s.done ? "text-slate-600" : "text-green-300"}>wł.{s.fiber_out}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          );
        })()}

        {full.work_plan?.splitters?.length > 0 && (
          <div className="bg-slate-800/60 border border-fuchsia-700/50 rounded-xl p-4 space-y-2" data-testid="monter-splitter-plan">
            <div className="font-semibold text-fuchsia-300 flex items-center gap-2"><Waypoints size={16} /> Splittery do zainstalowania ({full.work_plan.splitters.length})</div>
            {full.work_plan.splitters.map((s, i) => (
              <div key={i} className="text-xs flex items-center gap-2 bg-slate-900/50 rounded px-2 py-1.5" data-testid="splitter-plan-item">
                <span className="font-semibold text-fuchsia-200">{s.split_type}{s.split_ratio ? ` (${s.split_ratio})` : ""}</span>
                <span className="text-slate-300">Mufa {s.closure_number}</span>
                {s.input_cable_id && <span className="text-slate-400 font-mono ml-auto">zasil. {s.input_cable_number}/{s.input_fiber}</span>}
              </div>
            ))}
            <p className="text-[11px] text-slate-400">Splittery dodadzą się do sieci po „Zakończ”.</p>
          </div>
        )}

        {full.work_plan?.cable?.geometry?.length > 1 && (
          <div className="bg-slate-800/60 border border-cyan-700/50 rounded-xl p-4 space-y-2" data-testid="monter-cable-plan">
            <div className="font-semibold text-cyan-300 flex items-center gap-2"><Cable size={16} /> Montaż kabla po trasie</div>
            <div className="text-xs text-slate-300">{full.work_plan.cable.name || "Nowy kabel"} · {full.work_plan.cable.fiber_count} wł.{full.work_plan.cable.cable_type ? ` · ${full.work_plan.cable.cable_type}` : ""} · ~{full.work_plan.cable.length_m} m</div>
            <div className="h-40 rounded-lg overflow-hidden" data-testid="monter-cable-map">
              <MapContainer center={full.work_plan.cable.geometry[0]} zoom={15} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap contributors" />
                <Polyline positions={full.work_plan.cable.geometry} pathOptions={{ color: "#22d3ee", weight: 4 }} />
              </MapContainer>
            </div>
            <a href={`https://www.google.com/maps/dir/?api=1&destination=${full.work_plan.cable.geometry[0][0]},${full.work_plan.cable.geometry[0][1]}`} target="_blank" rel="noreferrer" className="text-xs text-cyan-300 underline flex items-center gap-1"><Navigation size={13} /> Nawiguj do początku trasy</a>
            <p className="text-[11px] text-slate-400">Kabel zostanie utworzony po kliknięciu „Zakończ”.</p>
          </div>
        )}

        {full.wifi_installation_id && (
          <button onClick={() => setAimOpen(true)} data-testid="task-antenna-aim"
            className="w-full bg-purple-600 hover:bg-purple-500 rounded-xl py-3 font-semibold flex items-center justify-center gap-2">
            <Compass size={18} /> Ustaw antenę (kompas)
          </button>
        )}
        {aimOpen && <AntennaAim installationId={full.wifi_installation_id} onClose={() => { setAimOpen(false); load(); }} />}

        <button onClick={navigate} data-testid="task-navigate"
          className="w-full bg-blue-600 hover:bg-blue-500 rounded-xl py-3 font-semibold flex items-center justify-center gap-2">
          <Navigation size={18} /> Nawiguj do klienta
        </button>

        <div className="grid grid-cols-2 gap-3">
          <label className="bg-slate-700 hover:bg-slate-600 rounded-xl py-3 font-medium flex items-center justify-center gap-2 cursor-pointer" data-testid="task-photo">
            <Camera size={18} /> Zdjęcie
            <input type="file" accept="image/*" capture="environment" onChange={onPhoto} className="hidden" />
          </label>
          <button onClick={() => setSig(true)} data-testid="task-signature"
            className="bg-slate-700 hover:bg-slate-600 rounded-xl py-3 font-medium flex items-center justify-center gap-2">
            <PenLine size={18} /> Podpis
          </button>
        </div>

        <a href={`${API}/schedule/tasks/${task.id}/protocol.pdf?auth=${encodeURIComponent(dlToken())}`}
          target="_blank" rel="noreferrer" data-testid="task-protocol"
          className="w-full bg-slate-800 border border-slate-700 rounded-xl py-3 font-medium flex items-center justify-center gap-2">
          <FileText size={18} /> Protokół odbioru (PDF)
        </a>

        {ci.name && (
          <div data-testid="monter-speedtest">
            <SpeedTest onComplete={(r) => { api.post(`/schedule/tasks/${task.id}/speedtest`, r).then(() => toast.success("Zapisano pomiar do protokołu")).catch(() => {}); }} />
          </div>
        )}

        {files.length > 0 && (
          <div>
            <div className="text-xs text-slate-400 mb-2">Załączniki ({files.length})</div>
            <div className="grid grid-cols-3 gap-2">
              {files.map((f) => (
                <a key={f.id} href={`${API}/files/${f.id}`} target="_blank" rel="noreferrer" className="block">
                  <img src={`${API}/files/${f.id}`} alt="" className="w-full h-20 object-cover rounded-lg border border-slate-700" />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-800 p-3 flex gap-3">
        {full.status !== "podjete" && full.status !== "gotowe" && (
          <button onClick={() => act("accept")} disabled={busy} data-testid="task-accept"
            className="flex-1 bg-amber-500 text-black rounded-xl py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <PlayCircle size={18} />} Podejmij
          </button>
        )}
        {full.status !== "gotowe" && (
          <button onClick={() => act("complete")} disabled={busy} data-testid="task-complete"
            className="flex-1 bg-green-600 rounded-xl py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />} Zakończ
          </button>
        )}
        {full.status === "gotowe" && (
          <div className="flex-1 text-center text-green-400 py-3 font-semibold flex items-center justify-center gap-2"><CheckCircle2 size={18} /> Zlecenie zakończone</div>
        )}
      </div>
      {sig && <SignaturePad onSave={onSig} onCancel={() => setSig(false)} />}
      {viewClosure && <ObjectInfo module="closures" id={viewClosure.id} readOnly onClose={() => setViewClosure(null)} />}
    </div>
  );
}

function AbsenceModal({ onClose }) {
  const [type, setType] = useState("urlop");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!from || !to) { toast.error("Podaj daty od i do"); return; }
    setBusy(true);
    try { await api.post("/schedule/absences", { type, from, to, reason }); toast.success("Zgłoszono nieobecność"); onClose(true); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const inp = "w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-base outline-none focus:ring-2 focus:ring-cyan-500";
  return (
    <div className="fixed inset-0 z-[2000] bg-black/70 flex items-end sm:items-center justify-center p-4" data-testid="monter-absence-modal">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-100 flex items-center gap-2"><CalendarOff size={18} className="text-amber-400" /> Zgłoś nieobecność</h3>
          <button onClick={() => onClose(false)} className="text-slate-400" data-testid="monter-absence-close"><X size={20} /></button>
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1 block">Rodzaj</label>
          <select value={type} onChange={(e) => setType(e.target.value)} data-testid="monter-absence-type" className={inp}>
            <option value="urlop">Urlop</option>
            <option value="l4">L4 (chorobowe)</option>
            <option value="inne">Inne</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-xs text-slate-400 mb-1 block">Od</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="monter-absence-from" className={inp} /></div>
          <div><label className="text-xs text-slate-400 mb-1 block">Do</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="monter-absence-to" className={inp} /></div>
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1 block">Powód (opcjonalnie)</label>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="np. urlop wypoczynkowy" data-testid="monter-absence-reason" className={inp} />
        </div>
        <button onClick={submit} disabled={busy} data-testid="monter-absence-submit"
          className="w-full bg-amber-600 hover:bg-amber-500 disabled:opacity-60 rounded-lg py-2.5 font-semibold flex items-center justify-center gap-2 text-black">
          {busy && <Loader2 size={16} className="animate-spin" />} Wyślij zgłoszenie
        </button>
      </div>
    </div>
  );
}

function parseScan(text) {
  try {
    const u = new URL(text, window.location.origin);
    const m = u.pathname.match(/\/o\/([^/]+)\/([^/]+)/);
    if (m) return { module: m[1], id: decodeURIComponent(m[2]) };
    const mod = u.searchParams.get("open"); const id = u.searchParams.get("id");
    if (mod && id) return { module: mod, id };
  } catch {
    const m = String(text).match(/\/o\/([^/]+)\/([^/?#]+)/);
    if (m) return { module: m[1], id: m[2] };
  }
  return null;
}

function QrScanner({ onClose, onResult }) {
  const ref = useRef(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let cancelled = false;
    const el = ref.current;
    if (!el) return;
    const qr = new Html5Qrcode(el.id);
    qr.start({ facingMode: "environment" }, { fps: 10, qrbox: 250 },
      (text) => {
        const t = parseScan(text);
        if (t) { try { qr.stop().catch(() => {}); } catch { /* noop */ } if (!cancelled) onResult(t); }
        else setErr("Nieznany kod QR — zeskanuj etykietę obiektu FiberPass");
      },
      () => {}
    ).catch((e) => setErr("Brak dostępu do kamery: " + (e?.message || e)));
    return () => { cancelled = true; try { qr.stop().then(() => qr.clear()).catch(() => {}); } catch { /* noop */ } };
  }, [onResult]);
  return (
    <div className="fixed inset-0 z-[3000] bg-black flex flex-col" data-testid="monter-scanner">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <div className="font-semibold flex items-center gap-2"><QrCode size={18} /> Skanuj kod QR obiektu</div>
        <button onClick={onClose} data-testid="scanner-close" className="text-slate-300"><X size={22} /></button>
      </div>
      <div id="fp-qr-reader" ref={ref} className="flex-1 w-full" />
      {err && <div className="p-4 text-red-300 text-sm text-center" data-testid="scanner-error">{err}</div>}
      <div className="p-4 text-center text-slate-400 text-xs">Skieruj aparat na kod QR na mufie, splitrze lub innym obiekcie.</div>
    </div>
  );
}

export default function MonterApp() {
  const { user, loading, logout } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [sel, setSel] = useState(null);
  const [pushOn, setPushOn] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [locOn, setLocOn] = useState(typeof localStorage !== "undefined" && localStorage.getItem("fp_loc") === "1");
  const [showAbsence, setShowAbsence] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const [showScan, setShowScan] = useState(false);
  const [scanTarget, setScanTarget] = useState(null);
  const [nearby, setNearby] = useState(null);
  const [nearbyBusy, setNearbyBusy] = useState(false);
  const [routeOpt, setRouteOpt] = useState(null);
  const [routeBusy, setRouteBusy] = useState(false);
  const [outboxN, setOutboxN] = useState(0);
  const [showSet, setShowSet] = useState(false);
  const [nearRadius, setNearRadius] = useState(() => { try { return Number(localStorage.getItem("monter_nearby_radius")) || 100; } catch (e) { return 100; } });
  const NEAR_TYPES = [["closures", "Mufy"], ["clients", "Klienci"], ["splitters", "Splittery"], ["poles", "Słupy"], ["wells", "Studnie"], ["cabinets", "Szafy"], ["odf", "Szafy ODF"], ["patchpanels", "Przełącznice"], ["olt", "OLT"]];
  const [nearTypes, setNearTypes] = useState(() => { try { const v = JSON.parse(localStorage.getItem("monter_nearby_types") || "null"); return Array.isArray(v) && v.length ? v : NEAR_TYPES.map((x) => x[0]); } catch (e) { return NEAR_TYPES.map((x) => x[0]); } });
  const openNearby = () => {
    if (!navigator.geolocation) { toast.error("Brak dostępu do lokalizacji"); return; }
    setNearby([]); setNearbyBusy(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const rr = Number(nearRadius) || 100;
        const { data } = await api.get("/monter/nearby", { params: { lat: pos.coords.latitude, lng: pos.coords.longitude, radius: rr, types: (nearTypes || []).join(",") } });
        setNearby(data.objects || []);
      } catch { toast.error("Nie udało się pobrać obiektów"); setNearby([]); }
      finally { setNearbyBusy(false); }
    }, () => { toast.error("Nie udało się ustalić lokalizacji"); setNearbyBusy(false); setNearby(null); }, { enableHighAccuracy: true, timeout: 10000 });
  };
  useEffect(() => {
    const tick = () => api.get("/chat/unread").then((r) => setChatUnread(r.data.unread || 0)).catch(() => {});
    tick(); const id = setInterval(tick, 8000); return () => clearInterval(id);
  }, []);
  const toggleLoc = () => setLocOn((v) => { const nv = !v; try { localStorage.setItem("fp_loc", nv ? "1" : "0"); } catch (e) {} return nv; });

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const n = await flushOutbox(api);
      const { data } = await api.get("/schedule/tasks");
      setTasks(data || []); cacheTasks(data || []);
      setOutboxN(outboxSize());
      if (n) toast.success(`Zsynchronizowano ${n} zmian offline`);
    } catch (e) {
      const c = getCachedTasks();
      if (c.length) { setTasks(c); toast.info("Tryb offline — pokazuję zapisane zlecenia"); }
    }
    finally { setRefreshing(false); }
  }, []);

  useEffect(() => {
    setOutboxN(outboxSize());
    const refreshBadge = () => setOutboxN(outboxSize());
    const onOnline = async () => { const n = await flushOutbox(api); setOutboxN(outboxSize()); if (n) { toast.success(`Zsynchronizowano ${n} zmian offline`); load(); } };
    window.addEventListener("online", onOnline);
    window.addEventListener("fp-outbox", refreshBadge);
    return () => { window.removeEventListener("online", onOnline); window.removeEventListener("fp-outbox", refreshBadge); };
  }, [load]);

  useEffect(() => { registerServiceWorker(); }, []);

  // Udostępnianie lokalizacji: wysyłka co 30 s gdy przełącznik włączony i apka otwarta.
  // Serwer sam zapisuje tylko w dni/godziny pracy montera (poza grafikiem ignoruje).
  useEffect(() => {
    if (!user || user.role !== "monter" || !locOn || !navigator.geolocation) return;
    const tick = () => navigator.geolocation.getCurrentPosition(
      (pos) => api.post("/schedule/monter/location", { lat: pos.coords.latitude, lng: pos.coords.longitude }).catch(() => {}),
      () => {}, { enableHighAccuracy: true, maximumAge: 20000, timeout: 15000 });
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [user, locOn]);

  // Heartbeat: informuje serwer, że monter jest zalogowany (online) — co 60 s.
  useEffect(() => {
    if (!user || user.role !== "monter") return;
    const beat = () => api.post("/schedule/monter/heartbeat").catch(() => {});
    beat();
    const id = setInterval(beat, 60000);
    return () => clearInterval(id);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    load();
    (async () => {
      try {
        const reg = await navigator.serviceWorker?.getRegistration();
        const sub = reg && (await reg.pushManager.getSubscription());
        setPushOn(!!sub);
      } catch (e) {}
    })();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [user, load]);

  if (loading) return <div className="min-h-screen bg-[#0F172A] text-slate-400 flex items-center justify-center">Ładowanie…</div>;
  if (!user) return <LoginScreen />;
  if (sel) return <TaskDetail task={sel} onBack={() => { setSel(null); load(); }} onChanged={load} />;

  const togglePush = async () => {
    try {
      if (pushOn) { await disablePush(); setPushOn(false); toast.success("Powiadomienia wyłączone"); }
      else { await enablePush(); setPushOn(true); toast.success("Powiadomienia włączone ✅"); }
    } catch (err) { toast.error(err.message || "Błąd powiadomień"); }
  };
  const monterLogout = async () => {
    try { await api.post("/schedule/monter/logout"); } catch (e) {}
    logout();
  };
  const testPush = async () => {
    try {
      const { data } = await api.post("/push/test");
      if (data.sent) toast.success("Wysłano testowe powiadomienie");
      else {
        const reason = (data.errors && data.errors[0]) ? data.errors[0] : "Brak aktywnej subskrypcji — włącz powiadomienia";
        toast.warning(reason, { duration: 12000 });
      }
    }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const activeTasks = tasks.filter((t) => t.status !== "gotowe");
  const todayTasks = activeTasks.filter((t) => t.scheduled_date === today);
  const geoTasks = activeTasks.filter((t) => t.client_info?.lat != null || t.lat != null);
  const centre = geoTasks[0] ? [geoTasks[0].client_info?.lat ?? geoTasks[0].lat, geoTasks[0].client_info?.lng ?? geoTasks[0].lng] : [52.1, 19.4];

  const optimizeRoute = () => {
    if (!navigator.geolocation) { toast.error("Brak dostępu do lokalizacji"); return; }
    if (!activeTasks.length) { toast.info("Brak aktywnych zleceń"); return; }
    setRouteBusy(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { data } = await api.post("/schedule/route-optimize", { start: { lat: pos.coords.latitude, lng: pos.coords.longitude }, task_ids: activeTasks.map((tt) => tt.id) });
        setRouteOpt(data);
        if (!data.ordered?.length) toast.info("Brak zleceń ze współrzędnymi");
      } catch { toast.error("Nie udało się wyznaczyć trasy"); }
      finally { setRouteBusy(false); }
    }, () => { toast.error("Nie udało się ustalić lokalizacji"); setRouteBusy(false); }, { enableHighAccuracy: true, timeout: 10000 });
  };

  const card = (t) => {
    const st = STATUS[t.status] || STATUS.zaplanowane;
    return (
      <button key={t.id} onClick={() => setSel(t)} data-testid={`monter-task-${t.id}`}
        className="w-full text-left bg-slate-800/60 border border-slate-700 rounded-xl p-4 active:bg-slate-800">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-slate-400">{t.task_no} · {t.type_label}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
        </div>
        <div className="font-semibold">{t.title}</div>
        {t.address && <div className="text-sm text-slate-400 mt-1 flex items-center gap-1"><MapPin size={13} /> {t.address}</div>}
        <div className="text-xs text-slate-500 mt-1">🗓 {t.scheduled_date} {t.scheduled_time}</div>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-[#0F172A] text-slate-100 pb-6" data-testid="monter-home">
      <div className="sticky top-0 z-10 bg-slate-900 border-b border-slate-800 px-4 py-3">
        <div className="flex items-center gap-3">
          <img src="/icon-192.png" alt="" className="w-8 h-8 rounded-lg" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate">{user.name}</div>
            <div className="text-xs text-slate-400">Monter</div>
          </div>
        </div>
        <div className="flex items-center flex-wrap gap-2 mt-2 justify-end">
          {user.role === "monter" && (
            <>
              <button onClick={() => setShowAbsence(true)} data-testid="monter-absence-btn"
                className="p-2 rounded-lg bg-slate-700" title="Zgłoś nieobecność"><CalendarOff size={18} /></button>
              <button onClick={toggleLoc} data-testid="monter-loc-toggle"
                className={`p-2 rounded-lg ${locOn ? "bg-yellow-600 text-black" : "bg-slate-700"}`} title="Udostępniaj lokalizację">
                {locOn ? <Locate size={18} /> : <LocateOff size={18} />}
              </button>
            </>
          )}
          <button onClick={() => setShowScan(true)} data-testid="monter-scan-btn"
            className="p-2 rounded-lg bg-green-600" title="Skanuj QR obiektu"><QrCode size={18} /></button>
          <button onClick={openNearby} data-testid="monter-nearby-btn"
            className="p-2 rounded-lg bg-blue-600" title="Obiekty w pobliżu"><MapPin size={18} /></button>
          <button onClick={() => setShowChat(true)} data-testid="monter-chat-btn"
            className={`relative p-2 rounded-lg ${chatUnread > 0 ? "bg-cyan-600 animate-pulse" : "bg-slate-700"}`} title="Wiadomości">
            <MessageSquare size={18} />
            {chatUnread > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] leading-none rounded-full px-1 py-0.5" data-testid="monter-chat-badge">{chatUnread}</span>}
          </button>
          <button onClick={togglePush} data-testid="monter-push-toggle"
            className={`p-2 rounded-lg ${pushOn ? "bg-cyan-600" : "bg-slate-700"}`}>
            {pushOn ? <Bell size={18} /> : <BellOff size={18} />}
          </button>
          <button onClick={() => setShowSet(true)} data-testid="monter-settings-btn"
            className="p-2 rounded-lg bg-slate-700" title="Ustawienia"><Settings size={18} /></button>
          <button onClick={monterLogout} data-testid="monter-logout" className="p-2 rounded-lg bg-slate-700"><LogOut size={18} /></button>
        </div>
      </div>

      <InstallBanner />

      {!pushSupported() && (
        <div className="mx-4 mt-3 text-xs bg-amber-500/10 border border-amber-500/40 text-amber-300 rounded-lg p-2">
          Ta przeglądarka nie obsługuje powiadomień. Na iPhone dodaj aplikację do ekranu głównego (iOS 16.4+).
        </div>
      )}

      {geoTasks.length > 0 && (
        <div className="h-56 w-full mt-1" data-testid="monter-overview-map">
          <MapContainer center={centre} zoom={11} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap contributors" />
            {geoTasks.map((t) => {
              const la = t.client_info?.lat ?? t.lat, ln = t.client_info?.lng ?? t.lng;
              if (la == null || ln == null) return null;
              return (
                <Marker key={t.id} position={[la, ln]} icon={pin(TYPE_COLOR[t.type] || "#22d3ee")}
                  eventHandlers={{ click: () => setSel(t) }}>
                  <Popup>{t.title}<br />{t.client_info?.name || t.address}</Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </div>
      )}

      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-bold flex items-center gap-2"><ClipboardList size={18} className="text-cyan-400" /> Moje zlecenia</h2>
          <div className="flex items-center gap-2">
            {outboxN > 0 && <span data-testid="monter-outbox-badge" title="Zmiany oczekujące na synchronizację" className="text-[11px] bg-amber-600/80 text-white rounded-full px-2 py-0.5 flex items-center gap-0.5">↑ {outboxN}</span>}
            <button onClick={optimizeRoute} disabled={routeBusy} data-testid="monter-optimize-route" className="text-xs flex items-center gap-1 bg-cyan-700/70 hover:bg-cyan-600 rounded-lg px-2.5 py-1.5 disabled:opacity-60">
              {routeBusy ? <Loader2 size={13} className="animate-spin" /> : <Waypoints size={13} />} Trasa
            </button>
            <button onClick={testPush} className="text-xs text-slate-400 underline" data-testid="monter-push-test">Test push</button>
            <button onClick={load} data-testid="monter-refresh" className="p-1.5 rounded-lg bg-slate-800">
              <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {routeOpt && routeOpt.ordered?.length > 0 && (
          <div className="bg-slate-800/60 border border-cyan-700/50 rounded-xl p-3 space-y-1.5" data-testid="monter-route-panel">
            <div className="flex items-center justify-between text-xs">
              <span className="text-cyan-300 font-semibold">Trasa od Twojej lokalizacji · {routeOpt.total_km} km</span>
              <button onClick={() => setRouteOpt(null)} className="text-slate-400" data-testid="monter-route-close"><X size={14} /></button>
            </div>
            {routeOpt.ordered.map((p, i) => (
              <div key={p.id} className="flex items-center gap-2 text-sm" data-testid={`monter-route-stop-${i}`}>
                <span className="w-5 h-5 rounded-full bg-cyan-600 text-white text-[11px] flex items-center justify-center flex-shrink-0">{i + 1}</span>
                <button onClick={() => { const tt = tasks.find((x) => x.id === p.id); if (tt) setSel(tt); }} className="flex-1 text-left truncate">
                  <span className="text-slate-200">{p.client_name || p.title || p.task_no}</span>
                  <span className="text-slate-500 text-xs"> · {p.leg_km} km</span>
                </button>
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`} target="_blank" rel="noreferrer" className="text-cyan-400 flex items-center" data-testid={`monter-route-nav-${i}`}><Navigation size={14} /></a>
              </div>
            ))}
            {routeOpt.skipped > 0 && <div className="text-[11px] text-amber-400">Pominięto {routeOpt.skipped} zleceń bez współrzędnych.</div>}
          </div>
        )}

        {todayTasks.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-semibold text-cyan-400 uppercase">Dziś</div>
            {todayTasks.map(card)}
          </div>
        )}
        <div className="space-y-2">
          <div className="text-xs font-semibold text-slate-500 uppercase">Wszystkie</div>
          {activeTasks.length === 0 && <div className="text-slate-500 text-sm text-center py-8">Brak przydzielonych zleceń.</div>}
          {activeTasks.filter((t) => t.scheduled_date !== today).map(card)}
        </div>
      </div>
      {showAbsence && <AbsenceModal onClose={() => setShowAbsence(false)} />}
      {showChat && <MonterChat user={user} onClose={() => { setShowChat(false); api.get("/chat/unread").then((r) => setChatUnread(r.data.unread || 0)).catch(() => {}); }} />}
      {showScan && <QrScanner onClose={() => setShowScan(false)} onResult={(t) => { setShowScan(false); setScanTarget(t); }} />}
      {scanTarget && <ObjectInfo module={scanTarget.module} id={scanTarget.id} onClose={() => setScanTarget(null)} />}
      {showSet && (
        <div className="fixed inset-0 z-[2000] bg-black/70 flex items-end sm:items-center justify-center p-4" data-testid="monter-settings-modal" onClick={() => setShowSet(false)}>
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-100 flex items-center gap-2"><Settings size={18} className="text-slate-300" /> Ustawienia</h3>
              <button onClick={() => setShowSet(false)} className="text-slate-400" data-testid="monter-settings-close"><X size={20} /></button>
            </div>
            <label className="text-xs text-slate-400 mb-1 block">Zasięg wykrywania obiektów w pobliżu [m]</label>
            <div className="flex items-center gap-2">
              <input type="number" min="5" max="2000" value={nearRadius}
                onChange={(e) => setNearRadius(e.target.value)} data-testid="monter-nearby-radius"
                className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm" />
              <span className="text-slate-500 text-sm">m</span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Obiekty w tym promieniu od Twojej lokalizacji będą pokazywane po kliknięciu „Obiekty w pobliżu".</p>
            <label className="text-xs text-slate-400 mb-1 mt-4 block">Jakie obiekty wyszukiwać</label>
            <div className="grid grid-cols-2 gap-1.5" data-testid="monter-types">
              {NEAR_TYPES.map(([key, label]) => {
                const on = nearTypes.includes(key);
                return (
                  <button key={key} type="button" data-testid={`monter-type-${key}`}
                    onClick={() => setNearTypes((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key])}
                    className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm border ${on ? "bg-cyan-600/20 border-cyan-500 text-cyan-200" : "bg-slate-800 border-slate-700 text-slate-400"}`}>
                    <span className={`w-3.5 h-3.5 rounded-sm border ${on ? "bg-cyan-500 border-cyan-400" : "border-slate-500"}`} />
                    {label}
                  </button>
                );
              })}
            </div>
            <button onClick={() => { const v = Math.min(2000, Math.max(5, Number(nearRadius) || 100)); setNearRadius(v); const tps = nearTypes.length ? nearTypes : NEAR_TYPES.map((x) => x[0]); setNearTypes(tps); try { localStorage.setItem("monter_nearby_radius", String(v)); localStorage.setItem("monter_nearby_types", JSON.stringify(tps)); } catch (e) {} toast.success(`Zapisano: ${v} m, ${tps.length} typów`); setShowSet(false); }}
              data-testid="monter-settings-save" className="w-full mt-4 bg-cyan-600 hover:bg-cyan-500 rounded-lg py-2 text-sm font-medium">Zapisz</button>
          </div>
        </div>
      )}
      {nearby !== null && (
        <div className="fixed inset-0 z-[2000] bg-black/70 flex items-end sm:items-center justify-center p-4" data-testid="monter-nearby-modal" onClick={() => setNearby(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md p-4 max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-100 flex items-center gap-2"><MapPin size={18} className="text-blue-400" /> Obiekty w pobliżu</h3>
              <button onClick={() => setNearby(null)} className="text-slate-400" data-testid="monter-nearby-close"><X size={20} /></button>
            </div>
            {nearbyBusy ? (
              <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Ustalam pozycję…</div>
            ) : nearby.length === 0 ? (
              <p className="text-slate-400 text-sm">Brak obiektów w promieniu 200 m.</p>
            ) : (
              <div className="space-y-1.5">
                {nearby.map((o) => (
                  <button key={`${o.module}-${o.id}`} data-testid={`nearby-item-${o.id}`}
                    onClick={() => { setNearby(null); setScanTarget({ module: o.module, id: o.id }); }}
                    className="w-full text-left bg-slate-800 hover:bg-slate-700 rounded-lg px-3 py-2 flex items-center justify-between gap-2">
                    <span className="min-w-0"><span className="text-slate-200 font-medium">{o.label}</span> <span className="text-slate-500 text-xs">· {o.kind}</span></span>
                    <span className="text-cyan-300 text-xs font-mono shrink-0">{o.dist_m} m</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
